import { useMemo } from 'react';

import { useQuery } from '@apollo/client';

import type { Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import type { UserFilter } from 'bundles/enterprise-admin-user-filters/types';
import { mapUserFilter } from 'bundles/enterprise-admin-user-filters/utils';
import EnterpriseUserAdminViewQueryByFilter from 'bundles/enterprise-admin-users/queries/EnterpriseUserAdminViewQueryByFilter.graphql';
import type {
  EnterpriseUserAdminViewQueryByFilterQuery,
  EnterpriseUserAdminViewQueryByFilterQueryVariables,
} from 'bundles/enterprise-admin-users/queries/__generated__/EnterpriseUserAdminViewQueryByFilter';

export const AUDIENCE_LEARNERS_PAGE_SIZE = 7;

/**
 * A ready-to-run learner-list query: the org + the client `UserFilter` that
 * defines the cohort (+ optional manually-checked identifiers). The hooks run it
 * through `mapUserFilter` — the SAME payload util the existing messaging/learners
 * pages use — so the list, count, and saved filter all derive from one source.
 */
export type LearnerListAudience = {
  orgId: string;
  userFilter: UserFilter;
  /** Mixed ids/emails to narrow the cohort to specific learners (manual selection). */
  userIdentifiers?: string[];
};

type Params = {
  /** Absent for the non-filter (manual) flow — the query is then skipped. */
  audience?: LearnerListAudience;
  /** 1-indexed page. */
  page: number;
  pageSize?: number;
  /** Server-side name/email search across the cohort. */
  search?: string;
};

/**
 * Fetches the learners for an audience with server-side pagination and search,
 * via the shared `EnterpriseUserAdminView.queryByFilter` query (the exact same
 * call the existing messaging recipient list + learners table use). The filter
 * payload is built with `mapUserFilter`. Backs the learner-list screens through
 * {@link useServerLearnerPaging}.
 */
export const useAudienceLearners = ({ audience, page, pageSize = AUDIENCE_LEARNERS_PAGE_SIZE, search }: Params) => {
  const filter = useMemo(
    // No audience → manual flow; the query is skipped, so this placeholder is never sent.
    () =>
      audience
        ? mapUserFilter(audience.userFilter, undefined, undefined, search || undefined, audience.userIdentifiers)
        : { primaryFilter: {} },
    [audience, search]
  );

  const { data, loading, error } = useQuery<
    EnterpriseUserAdminViewQueryByFilterQuery,
    EnterpriseUserAdminViewQueryByFilterQueryVariables
  >(EnterpriseUserAdminViewQueryByFilter, {
    variables: { orgId: audience?.orgId ?? '', filter, limit: pageSize, cursor: String((page - 1) * pageSize) },
    context: { clientName: 'gatewayGql' },
    // `no-cache`: each page is a fresh fetch of exactly `limit` rows. The
    // gateway's queryByFilter can merge paginated results in the normalized
    // cache, which would accumulate rows across page navigation.
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
    skip: !audience,
  });

  const learners: Learner[] = useMemo(
    () =>
      // Use email as the learner id because `cohortOverride.learnersRemoved` /
      // `learnersAdded` (where the delta ultimately lands) expects an array of
      // email strings, not userIds.
      (data?.EnterpriseUserAdminView?.queryByFilter.elements ?? []).map((element) => ({
        id: element.email,
        name: element.fullName,
        email: element.email,
      })),
    [data]
  );

  const total = data?.EnterpriseUserAdminView?.queryByFilter.pagination?.totalElements ?? 0;

  return { learners, total, loading, error };
};

export default useAudienceLearners;
